import { useMemo } from 'react'

import {
  Descriptions,
  Divider,
  Header,
  IconChevronDoubleRight,
  Space,
  SubText,
  Text,
  TransparentIconButton,
} from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useProcurifyIntl } from '@webapp/hooks'
import { CatalogItemDetailsGallery } from './CatalogItemDetailsGallery'
import { CatalogItemDetailsSparkle } from './CatalogItemDetailsSparkle'
import messages from './messages'
import {
  CatalogItemTagVariants,
  resolveInfoBadges,
} from '../../value-objects/CatalogItemTag'
import {
  type ConversationCatalogItemDelivery,
  type ConversationCatalogItemRating,
  type ConversationCatalogItemVendorBadge,
  type ConversationVendor,
  type ConversationVolumePricing,
} from '../../value-objects/ConversationTurnItem'
import { CatalogItemDeliveryLabel } from '../CatalogItemDeliveryLabel'
import { CatalogItemDetailsCart } from '../CatalogItemDetailsCart'
import { CatalogItemRating } from '../CatalogItemRating'
import { CatalogItemTag, type ICatalogItemTagProps } from '../CatalogItemTag'
import { VendorLogoBadge } from '../VendorLogoBadge'
import { CatalogItemDetailsAnalysisStyled } from './styles/CatalogItemDetailsAnalysisStyled'
import { CatalogItemDetailsAnalysisTextStyled } from './styles/CatalogItemDetailsAnalysisTextStyled'
import { CatalogItemDetailsBodyStyled } from './styles/CatalogItemDetailsBodyStyled'
import { CatalogItemDetailsDescriptionsStyled } from './styles/CatalogItemDetailsDescriptionsStyled'
import { CatalogItemDetailsFooterStyled } from './styles/CatalogItemDetailsFooterStyled'
import { CatalogItemDetailsHeaderStyled } from './styles/CatalogItemDetailsHeaderStyled'
import { CatalogItemDetailsLayoutStyled } from './styles/CatalogItemDetailsLayoutStyled'
import { CatalogItemDetailsPriceStyled } from './styles/CatalogItemDetailsPriceStyled'
import { CatalogItemDetailsSectionStyled } from './styles/CatalogItemDetailsSectionStyled'

export interface ICatalogItemDetail {
  label: string
  value: string
}

export interface ICatalogItemDetailsProps {
  img?: string
  images?: string[]
  title: string
  sku: string
  tag: ICatalogItemTagProps
  price: number
  currency?: CurrencyName
  vendor?: ConversationVendor
  rating?: ConversationCatalogItemRating
  vendorBadges?: ConversationCatalogItemVendorBadge[]
  listPrice?: number
  volumePricing?: ConversationVolumePricing
  agentAnalysis: string
  catalogDetails: ICatalogItemDetail[]
  delivery?: ConversationCatalogItemDelivery
  quantity: number
  estimatedTotal: number
  onQuantityChange: (quantity: number) => void
  onAddToRequest: () => void
  onCollapse: () => void
}

export const CatalogItemDetails = ({
  img,
  images,
  title,
  sku,
  tag,
  price,
  currency,
  vendor,
  rating,
  vendorBadges = [],
  listPrice,
  volumePricing,
  agentAnalysis,
  catalogDetails,
  delivery,
  quantity,
  estimatedTotal,
  onQuantityChange,
  onAddToRequest,
  onCollapse,
}: ICatalogItemDetailsProps) => {
  const intl = useProcurifyIntl()

  const galleryImages = useMemo(() => {
    if (images?.length) return images
    return img ? [img] : []
  }, [images, img])

  const infoBadges = resolveInfoBadges(vendorBadges)

  const renderSource = () => (
    <Space direction='horizontal' size='small' align='center' flexWrap>
      {vendor?.logoUrl ? (
        <VendorLogoBadge vendor={vendor} />
      ) : (
        <CatalogItemTag icon={tag.icon} text={tag.text} />
      )}
      {infoBadges.map((badge) => (
        <CatalogItemTag
          key={badge.slot}
          icon={badge.icon}
          text={badge.text}
          logoUrl={badge.logoUrl}
          variant={badge.variant}
        />
      ))}
    </Space>
  )

  const renderPrice = () => (
    <Space direction='vertical' size='small'>
      <Space direction='horizontal' size='small' align='center'>
        <CatalogItemDetailsPriceStyled className='fs-mask'>
          <Price inline price={price} currency={currency} />
        </CatalogItemDetailsPriceStyled>
        {!!listPrice && (
          <SubText inline noWrap color='textSecondary' className='fs-mask'>
            {intl.formatMessage(messages.listPriceLabel)}{' '}
            <s>
              <Price inline price={listPrice} currency={currency} />
            </s>
          </SubText>
        )}
      </Space>
      {volumePricing?.savingsHint && (
        <Space direction='horizontal' size='xsmall' align='center'>
          <CatalogItemTag
            text={intl.formatMessage(messages.savingsHint, {
              pct: volumePricing.savingsHint.savingsPct,
              label: volumePricing.savingsHint.label,
            })}
            variant={CatalogItemTagVariants.MUTED}
          />
        </Space>
      )}
      {!!volumePricing?.tiers?.length && (
        <Space direction='vertical' size='xsmall'>
          <Text bold>{intl.formatMessage(messages.volumePricingTitle)}</Text>
          {volumePricing.tiers.map((tier) => (
            <Space key={tier.label} direction='horizontal' align='center'>
              <Space.Item grow={1}>
                <SubText color='textSecondary'>{tier.label}</SubText>
              </Space.Item>
              <Text bold className='fs-mask'>
                <Price inline price={tier.unitPrice} currency={tier.currency} />
                {intl.formatMessage(messages.perUnitSuffix)}
              </Text>
            </Space>
          ))}
        </Space>
      )}
      {delivery && <CatalogItemDeliveryLabel delivery={delivery} />}
    </Space>
  )

  return (
    <CatalogItemDetailsLayoutStyled>
      <CatalogItemDetailsHeaderStyled>
        <TransparentIconButton
          iconComponent={<IconChevronDoubleRight size={24} block aria-hidden />}
          aria-label={intl.formatMessage(messages.collapseAriaLabel)}
          analyticsId='catalog-item-details-collapse-button'
          onClick={onCollapse}
        />
      </CatalogItemDetailsHeaderStyled>

      <CatalogItemDetailsBodyStyled>
        <div>
          <Space direction='vertical' size='medium'>
            <CatalogItemDetailsGallery images={galleryImages} />
            {renderSource()}
            <Space direction='vertical' size='xsmall'>
              <Header as='h4'>
                <span className='fs-mask'>{title}</span>
              </Header>
              {rating && (
                <CatalogItemRating
                  value={rating.value}
                  count={rating.count}
                  scaleMax={rating.scaleMax}
                />
              )}
              <Text color='darkGrey' className='fs-mask'>
                {intl.formatMessage(messages.sku, { sku })}
              </Text>
            </Space>
            <Divider />
            {renderPrice()}
            <Divider />
          </Space>

          <CatalogItemDetailsSectionStyled>
            <Space direction='vertical' size='medium'>
              <Space direction='horizontal' size='small' align='center'>
                <CatalogItemDetailsSparkle />
                <Header as='h5'>
                  {intl.formatMessage(messages.agentAnalysisTitle)}
                </Header>
              </Space>
              <CatalogItemDetailsAnalysisStyled>
                <CatalogItemDetailsAnalysisTextStyled className='fs-mask'>
                  {agentAnalysis}
                </CatalogItemDetailsAnalysisTextStyled>
              </CatalogItemDetailsAnalysisStyled>
            </Space>
          </CatalogItemDetailsSectionStyled>

          <CatalogItemDetailsSectionStyled>
            <Space direction='vertical' size='small'>
              <Header as='h5'>
                {intl.formatMessage(messages.catalogDetailsTitle)}
              </Header>
              <CatalogItemDetailsDescriptionsStyled>
                <Descriptions valueWidth='fill'>
                  {catalogDetails.map((detail) => (
                    <Descriptions.Item
                      key={`${detail.label}-${detail.value}`}
                      label={detail.label}
                    >
                      {detail.value}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </CatalogItemDetailsDescriptionsStyled>
            </Space>
          </CatalogItemDetailsSectionStyled>
        </div>
      </CatalogItemDetailsBodyStyled>

      <CatalogItemDetailsFooterStyled>
        <CatalogItemDetailsCart
          quantity={quantity}
          estimatedTotal={estimatedTotal}
          currency={currency}
          onQuantityChange={onQuantityChange}
          onAddToRequest={onAddToRequest}
        />
      </CatalogItemDetailsFooterStyled>
    </CatalogItemDetailsLayoutStyled>
  )
}
